import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Button from './Button.jsx';
import { message, Select as AntSelect } from 'antd';

import axios from 'axios';
import md5 from 'md5';
import FloatingUploadModal from './FloatingUploadModal.jsx';
import documentChunkingProgressService from '../services/documentChunkingProgressService.js';
import documentProgressService from '../services/documentProgressService.js';
import { graphqlRequest } from '../services/graphqlClient.js';
import { createStorageLink, updateStorageLink } from '../graphql/mutations.js';
import StandardDropdown from './compliance-ui/StandardDropdown.jsx';

import {
  getIndustryOptions,
  getCategoryOptions,
  getLevel4Options,
  getLevel5Options,
  getLevel6Options,
} from '../data/requirementHierarchy.js';
import { useAuth } from '../auth.js';
import {
  folderUploadErrorMessage,
  resolveSharePointAccessTokenForUpload,
} from '../utils/sharepointFolderUpload.js';


const CHUNK_JOBS_KEY = 'codiii_active_chunk_jobs';

function readActiveJobs() {
  try {
    return JSON.parse(localStorage.getItem(CHUNK_JOBS_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeActiveJobs(jobsObj) {
  localStorage.setItem(CHUNK_JOBS_KEY, JSON.stringify(jobsObj || {}));
}

function upsertActiveJob(job) {
  const jobs = readActiveJobs();
  jobs[job.jobId] = job;
  writeActiveJobs(jobs);
}

function removeActiveJob(jobId) {
  const jobs = readActiveJobs();
  delete jobs[jobId];
  writeActiveJobs(jobs);
}

const getLinkType = (url) => {
  if (!url) return 'other';
  if (url.includes('drive.google.com')) return 'google';
  if (url.includes('sharepoint.com')) return 'sharepoint';
  if (url.includes('dropbox.com')) return 'dropbox';
  return 'other';
};

const getLinkName = (url) => {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    return pathParts[pathParts.length - 1] || 'Shared Link';
  } catch {
    return 'Shared Link';
  }
};
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
async function getMsGraphAccessToken(idToken) {
  if (!idToken) throw new Error("Missing id_token in localStorage");

  const res = await fetch(
    `${API_BASE}/api/ms/graph/token`, 
    {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}` },
    }
  );

  const text = await res.text();
  if (!res.ok) {
    throw new Error(`ms/graph/token failed: ${res.status} ${text}`);
  }

  const data = JSON.parse(text);
  if (!data?.access_token) throw new Error("ms/graph/token returned no access_token");
  return data.access_token;
}


const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;


const DialogOverlay = styled.div`
  margin-top: 180px;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;       
  justify-content: center;   
  z-index: 99999;            
  animation: ${fadeIn} 0.3s ease;
  padding: 20px;
`;

const DialogContent = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  width: 100%;
  max-width: 600px;
  max-height: calc(100vh - 40px);  
  overflow: hidden;                 
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${slideUp} 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  font-family: 'Montserrat', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 640px) {
    max-width: 95%;
    border-radius: 16px;
  }
`;


const DialogHeader = styled.div`
  padding: 24px 28px;
  border-bottom: 2px solid rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
`;

const DialogTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #333;
  letter-spacing: -0.3px;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(0, 0, 0, 0.05);
  color: #666;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 300;

  &:hover {
    background: rgba(233, 112, 36, 0.1);
    color: #e97024;
    transform: rotate(90deg);
  }
`;


const DialogBody = styled.div`
  padding: 28px;
  flex: 1;                          
  max-height: none;                 
  overflow-y: auto;                 
  scrollbar-width: thin;
  scrollbar-color: rgba(233, 112, 36, 0.3) rgba(0, 0, 0, 0.05);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(233, 112, 36, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(233, 112, 36, 0.5);
  }

  @media (max-width: 640px) {
    padding: 20px;
  }
`;


const Footer = styled.div`
  padding: 20px 28px;
  border-top: 2px solid rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(255, 255, 255, 0.8);
  flex-shrink: 0;                   

  @media (max-width: 640px) {
    flex-direction: column-reverse;
    padding: 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
   &:last-child {
    margin-bottom: 0;   
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #444;
  margin-bottom: 8px;
  letter-spacing: -0.1px;

  ${(props) =>
    props.$required &&
    `
    &::after {
      content: ' *';
      color: #e97024;
    }
  `}
`;

const HelperText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 6px;
  line-height: 1.5;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  color: #333;
  background: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: #aaa;
  }

  &:focus {
    outline: none;
    border-color: #e97024;
    background: #ffffff;
    box-shadow: 0 4px 16px rgba(233, 112, 36, 0.15);
  }

  &:hover {
    border-color: rgba(233, 112, 36, 0.3);
  }

  &:disabled {
    background: rgba(0, 0, 0, 0.03);
    color: #999;
    cursor: not-allowed;
  }
`;

const CategorySelect = styled(AntSelect)`
  && .ant-select-selector {
    border-radius: 12px !important;
    border: 2px solid rgba(0, 0, 0, 0.1) !important;
    padding: 4px 12px !important;
    height: 44px !important;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.7) !important;
    transition: all 0.3s ease;
    box-shadow: none !important;
  }

  && .ant-select-selection-placeholder,
  && .ant-select-selection-item {
    font-size: 14px;
    color: #333;
  }

  && .ant-select-arrow {
    color: #666;
    font-size: 10px;
  }

  &&:hover .ant-select-selector {
    border-color: rgba(233, 112, 36, 0.3) !important;
  }

  &&.ant-select-open .ant-select-selector,
  &&.ant-select-focused .ant-select-selector {
    border-color: #e97024 !important;
    background: #ffffff !important;
    box-shadow: 0 4px 16px rgba(233, 112, 36, 0.15) !important;
  }

  &&.ant-select-disabled .ant-select-selector {
    background: rgba(0, 0, 0, 0.03) !important;
    color: #999;
    cursor: not-allowed;
  }
`;


const BaseButton = styled(Button)`
  padding: 12px 28px;
  border-radius: 12px;
  font-family: 'Montserrat', system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.1px;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
`;

const CancelButton = styled(BaseButton)`
  background: rgba(0, 0, 0, 0.05);
  color: #666;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #333;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const SaveButton = styled(BaseButton)`
  background: linear-gradient(135deg, #e97024 0%, #ff8c42 100%);
  color: #fff;
  box-shadow: 0 4px 16px rgba(233, 112, 36, 0.3);

  &:hover {
    box-shadow: 0 8px 24px rgba(233, 112, 36, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const DeleteButton = styled(BaseButton)`
  background: rgba(248, 113, 113, 0.08);
  color: #b91c1c;
  border: 1px solid rgba(248, 113, 113, 0.6);
  margin-right: auto;

  &:hover {
    background: rgba(248, 113, 113, 0.16);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 640px) {
    width: 100%;
    margin-right: 0;
  }
`;

const RemoveLinkButton = styled.button`
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  font-size: 16px;
  padding: 2px 6px;
  border-radius: 999px;
  transition: all 0.2s ease;

  &:hover {
    color: #ef4444;
    background: #fee2e2;
  }
`;

const UndoButton = styled.button`
  border: none;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 999px;
  transition: all 0.2s ease;

  &:hover {
    color: #16a34a;
    background: #dcfce7;
  }
`;


const RequirementDetailsDialog = ({
  isOpen,
  onClose,
  requirement,
  onSave,
  onBuildFromDocument,
  columnId,
  defaultStatus,
  selectedProject,
  onChunkingStart,
  onChunkingUpdate,
  onChunkingDone,
  onChunkingError,
  onDeleteRequirement,
  onLinksChanged,
}) => {
  const auth = useAuth();

  const currentUserId = auth.user?.sub || null;

  const currentUserEmail = String(
    auth.user?.profile?.email || auth.user?.email || ''
  )
    .trim()
    .toLowerCase() || null;


  const [phase, setPhase] = useState(
    (requirement?.metadata?.status || columnId || defaultStatus || 'design').toLowerCase()
  );
  const [industry, setIndustry] = useState(requirement?.metadata?.industry || '');
  const [category, setCategory] = useState(requirement?.metadata?.category || '');

  const [level4, setLevel4] = useState(
    requirement?.metadata?.hierarchy?.level4 ||
      requirement?.metadata?.hierarchy?.subSubCategory ||
      ''
  );
  const [level5, setLevel5] = useState(
    requirement?.metadata?.hierarchy?.level5 || ''
  );
  const [level6, setLevel6] = useState(
    requirement?.metadata?.hierarchy?.level6 || ''
  );

  const [folderUrl, setFolderUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingFromDrive, setIsUploadingFromDrive] = useState(false);
  const [uploadSource, setUploadSource] = useState('');
  const [existingLinks, setExistingLinks] = useState([]);
  const [linksToDelete, setLinksToDelete] = useState([]);

  const phaseLabel =
    phase === 'design'
      ? 'Design'
      : phase === 'build'
      ? 'Build'
      : phase === 'operate'
      ? 'Operate'
      : '';

  const industryOptions = getIndustryOptions(phaseLabel);
  const categoryOptions = getCategoryOptions(phaseLabel, industry);
  const level4Options = getLevel4Options(phaseLabel, industry, category);
  const level5Options = getLevel5Options(
    phaseLabel,
    industry,
    category,
    level4
  );
  const level6Options = getLevel6Options(
    phaseLabel,
    industry,
    category,
    level4,
    level5
  );

  useEffect(() => {
    const meta = requirement?.metadata || {};
    const hierarchy = meta.hierarchy || {};

    const normalizedPhase = (meta.status || columnId || defaultStatus || 'design').toLowerCase();

    setPhase(normalizedPhase);
    setIndustry(
      meta.industry ||
        hierarchy.industry ||
        hierarchy.mainCategory ||
        ''
    );
    setCategory(
      meta.category ||
        hierarchy.category ||
        hierarchy.subCategory ||
        ''
    );
    setLevel4(
      hierarchy.level4 ||
        hierarchy.subSubCategory ||
        ''
    );
    setLevel5(hierarchy.level5 || '');
    setLevel6(hierarchy.level6 || '');

    setLinksToDelete([]);

    let links = [];

    const addLinksFromStorage = (storage) => {
      if (!storage) return;
      if (Array.isArray(storage)) {
        links.push(...storage);
      } else if (storage && Array.isArray(storage.links)) {
        links.push(...storage.links);
      }
    };

    addLinksFromStorage(meta.storage);
    addLinksFromStorage(requirement?.storage);

    if (requirement?.storageLinks?.items?.length) {
      requirement.storageLinks.items.forEach((sl) => {
        if (!sl) return;
        links.push({
          id: sl.id,
          name: sl.name || sl.folderName || sl.url,
          type: sl.type || sl.linkType || getLinkType(sl.url),
          url: sl.url,
        });
      });
    }

    const seen = new Set();
    const deduped = [];
    for (const link of links) {
      if (!link) continue;
      const key = link.id || link.url;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      deduped.push(link);
    }

    setExistingLinks(deduped);
    setFolderUrl('');
  }, [requirement, columnId, defaultStatus]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const triggerFolderUpload = async (effectiveRequirement) => {
    const trimmed = folderUrl.trim();
    if (!trimmed) return;

    const requirementId =
      effectiveRequirement?.id ||
      requirement?.id ||
      effectiveRequirement?.requirementId;
    const projectId =
      effectiveRequirement?.projectId ||
      requirement?.projectId ||
      selectedProject?.id ||
      effectiveRequirement?.project_id;

    if (!requirementId || !projectId) {
      message.error(
        'Requirement was saved, but folder upload could not start (missing requirement/project id).'
      );
      return;
    }

    try {
      new URL(trimmed);
    } catch {
      message.error('Folder URL is not a valid URL.');
      return;
    }

    const linkType = getLinkType(trimmed);
    const linkName = getLinkName(trimmed);

    const formattedTypeMap = {
      google: 'Google Drive',
      sharepoint: 'SharePoint',
      dropbox: 'Dropbox',
      other: 'External Link',
    };
    const formattedType = formattedTypeMap[linkType] || 'External Link';

    const linkId = md5(`${trimmed}_${projectId}_${requirementId}`);

    const input = {
      id: linkId,
      name: linkName,
      type: linkType,
      url: trimmed,
      projectId,
      requirementId,
    };

    try {
      setIsUploadingFromDrive(true);
      setUploadSource(formattedType);

      try {
        await graphqlRequest({
          query: createStorageLink,
          variables: { input },
        });
      } catch (e) {
        const msg = String(e?.message || e);
        if (
          msg.includes('ConditionalCheckFailed') ||
          msg.includes('conditional request failed') ||
          msg.includes('already exists')
        ) {
          await graphqlRequest({
            query: updateStorageLink,
            variables: { input }, 
          });
        } else {
          throw e;
        }
      }


      message.loading(`Uploading from ${formattedType}...`, 2);

      const idToken = localStorage.getItem('id_token');

      let sharepointAccessToken;
      if (linkType === 'sharepoint') {
        sharepointAccessToken = await resolveSharePointAccessTokenForUpload(trimmed, idToken);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL_UPLOAD}/uploadFromDrive`,
        {
          folderUrl: trimmed,
          requirementId,
          projectId,
          companyId: selectedProject?.companyId,
          linkId,
          userId: currentUserId,
          userEmail: currentUserEmail,
          sharepointAccessToken,
        },
        {
          headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
        }
      );

      if (response.status === 200 || response.status === 202) {
        const jobId = `${projectId}_${requirementId}_${linkId}`;

        onChunkingStart?.(jobId);

        upsertActiveJob({
          jobId,
          projectId,
          requirementId,
          linkId,
          startedAt: Date.now(),
          lastProgress: 5,
          lastMessage: `Uploading from ${formattedType}...`,
        });

        documentChunkingProgressService.startPolling({
          jobId,
          statusUrl: process.env.REACT_APP_CHUNK_STATUS_URL,
          onUpdate: (state = {}) => {
            const progress = state.progress ?? 0;
            const msg = state.message ?? 'Indexing linked documents…';

            onChunkingUpdate?.(progress, msg);

            upsertActiveJob({
              jobId,
              projectId,
              requirementId,
              linkId,
              startedAt: Date.now(),
              lastProgress: progress,
              lastMessage: msg,
            });

            documentProgressService.broadcast({
              isVisible: true,
              currentStage: msg,
              stageProgress: progress,
              overallProgress: progress,
              documentTitle: effectiveRequirement?.title || 'Documents',
            });
          },
          onDone: () => {
            setIsUploadingFromDrive(false);
            onChunkingDone?.();
            message.success('Files uploaded and indexed successfully.');
            removeActiveJob(jobId);

            documentProgressService.broadcast({
              isVisible: false,
              currentStage: '',
              stageProgress: 0,
              overallProgress: 0,
              documentTitle: '',
            });
          },
          onError: (err) => {
            console.error('Upload/index error', err);
            setIsUploadingFromDrive(false);
            onChunkingError?.(err);
            message.error('Upload failed. Please try again.');
            removeActiveJob(jobId);

            documentProgressService.broadcast({
              isVisible: false,
              currentStage: '',
              stageProgress: 0,
              overallProgress: 0,
              documentTitle: '',
            });
          },
        });
      } else {
        message.warning('Upload may not have started correctly.');
        setIsUploadingFromDrive(false);
      }
    } catch (error) {
      console.error('Error starting folder upload:', error);
      message.error(folderUploadErrorMessage(error));
      setIsUploadingFromDrive(false);
    }
  };

  const handleRemoveLink = (key) => {
    setLinksToDelete((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const handleUndoRemoveLink = (key) => {
    setLinksToDelete((prev) => prev.filter((id) => id !== key));
  };

  const handleSave = async () => {
    if (!industry || !category) {
      message.error('Please select an industry and category.');
      return;
    }

    setIsSaving(true);

    const previousMetadata = requirement?.metadata || {};
    const trimmedFolderUrl = folderUrl.trim();

    const requirementId =
      requirement?.id || requirement?.requirementId || undefined;
    const projectId =
      requirement?.projectId ||
      selectedProject?.id ||
      requirement?.project_id ||
      undefined;

    if (linksToDelete.length > 0 && requirementId && projectId) {
      for (const key of linksToDelete) {
        const link = existingLinks.find((l) => (l.id || l.url) === key);
        if (!link?.id && !link?.url) {
          continue;
        }
        try {
          const s3LinkId = md5(`${link.url}_${projectId}_${requirementId}`);

          const resp = await axios.post(
            `${process.env.REACT_APP_DELETE_REQUIREMENT_FILES_URL}/delete-requirement-files`,
            {
              projectId,
              requirementId,
              linkId: s3LinkId,
            }
          );

          if (process.env.NODE_ENV === 'development') {
            console.log(
              `Deleted files for link ${link.id || link.url} (S3 linkId=${s3LinkId}):`,
              resp.data?.deleted?.length || 0,
              'files'
            );
          }
        } catch (err) {
          console.error(
            `Failed to delete S3 files for link ${link.id || link.url}`,
            err
          );
          message.warning(`Could not delete files for ${link.name || link.id || link.url}`);
        }
      }
    }

    let storage = existingLinks.filter(
      (link) => !linksToDelete.includes(link.id || link.url)
    );

    if (trimmedFolderUrl) {
      const linkType = getLinkType(trimmedFolderUrl);
      const linkName = getLinkName(trimmedFolderUrl);

      const linkId = md5(
        `${trimmedFolderUrl}_${projectId || ''}_${requirementId || ''}`
      );

      const newStorageEntry = {
        id: linkId,
        name: linkName,
        type: linkType,
        url: trimmedFolderUrl,
      };

      storage = [...storage, newStorageEntry];
    }

    const previousHierarchy = previousMetadata.hierarchy || {};
    const hierarchy = {
      ...previousHierarchy,
      phase: phaseLabel || previousHierarchy.phase,
      mainCategory: industry,
      subCategory: category,
      subSubCategory: level4 || previousHierarchy.subSubCategory || '',
      level4: level4 || previousHierarchy.level4 || '',
      level5: level5 || previousHierarchy.level5 || '',
      level6: level6 || previousHierarchy.level6 || '',
    };

    const descriptionMetadata = {
      ...previousMetadata,
      status: phase || previousMetadata.status || 'design',
      parameters: previousMetadata.parameters || [],
      hierarchy,
      storage,
      industry,
      category,
      level4,
      level5,
      level6,
    };

    const payload = {
      title: requirement?.title || '',
      description: requirement?.description || '',
      status: phase,
      industry,
      category,
      level4,
      level5,
      level6,
      parameters: descriptionMetadata.parameters,
      hierarchy: descriptionMetadata.hierarchy,
      storage,
      metadata: descriptionMetadata,
    };

    try {
      const maybeResult = onSave ? onSave(payload) : null;
      let effectiveRequirement = requirement || payload;

      if (maybeResult && typeof maybeResult.then === 'function') {
        effectiveRequirement = await maybeResult;
      } else if (maybeResult) {
        effectiveRequirement = maybeResult;
      }

      const effectiveRequirementId =
        effectiveRequirement?.id ||
        effectiveRequirement?.requirementId ||
        requirementId;

      if (effectiveRequirementId && typeof onLinksChanged === 'function') {
        onLinksChanged(effectiveRequirementId, storage);
      }

      if (trimmedFolderUrl) {
        await triggerFolderUpload(effectiveRequirement);
      }

      setLinksToDelete([]);
      setFolderUrl('');
      setIsSaving(false);
      onClose();
    } catch (err) {
      console.error('Error saving requirement:', err);
      message.error('Failed to save requirement.');
      setIsSaving(false);
    }
  };

  const handleDeleteRequirementClick = async () => {
    if (!requirement) {
      onClose();
      return;
    }

    const requirementId =
      requirement.id || requirement.requirementId || undefined;
    const projectId =
      requirement.projectId ||
      selectedProject?.id ||
      requirement.project_id ||
      undefined;

    if (!requirementId || !projectId) {
      message.error('Missing requirement or project id. Cannot delete.');
      return;
    }

    const confirmed = window.confirm(
      'Delete this requirement and all its linked folders? This cannot be undone.'
    );
    if (!confirmed) return;

    setIsSaving(true);

    try {
      if (existingLinks.length > 0) {
        for (const link of existingLinks) {
          if (!link?.url) continue;
          try {
            const s3LinkId = md5(`${link.url}_${projectId}_${requirementId}`);

            const resp = await axios.post(
              `${process.env.REACT_APP_DELETE_REQUIREMENT_FILES_URL}/delete-requirement-files`,
              {
                projectId,
                requirementId,
                linkId: s3LinkId,
              }
            );

            if (process.env.NODE_ENV === 'development') {
              console.log(
                `Deleted ALL files for requirement link ${link.id || link.url} (S3 linkId=${s3LinkId}):`,
                resp.data?.deleted?.length || 0,
                'files'
              );
            }
          } catch (err) {
            console.error(
              `Failed to delete S3 files for requirement link ${link.id || link.url}`,
              err
            );
          }
        }
      }

      if (onDeleteRequirement) {
        await onDeleteRequirement({
          requirementId,
          projectId,
          storageLinks: existingLinks,
        });
      }

      if (typeof onLinksChanged === 'function') {
        onLinksChanged(requirementId, []);
      }

      setIsSaving(false);
      onClose();
    } catch (err) {
      console.error('Error deleting requirement:', err);
      message.error('Failed to delete requirement.');
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <DialogOverlay onClick={handleOverlayClick}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>
              {requirement ? 'Edit Requirement' : 'Add Requirement'}
            </DialogTitle>
            <CloseButton type="button" onClick={onClose} aria-label="Close">
              ×
            </CloseButton>
          </DialogHeader>

          <DialogBody>
            <FormGroup>
              <Label>Phase</Label>
              <StandardDropdown
                value={phase}
                options={[
                  { value: 'design', label: 'Design' },
                  { value: 'build', label: 'Build' },
                  { value: 'operate', label: 'Operate' },
                ]}
                onChange={(value) => {
                  setPhase(value || 'design');
                  setIndustry('');
                  setCategory('');
                  setLevel4('');
                  setLevel5('');
                  setLevel6('');
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label $required>Industry</Label>
              <StandardDropdown
                value={industry}
                placeholder="Select industry..."
                disabled={!phaseLabel}
                options={industryOptions.map((opt) => ({
                  value: opt,
                  label: opt,
                }))}
                onChange={(value) => {
                  setIndustry(value || '');
                  setCategory('');
                  setLevel4('');
                  setLevel5('');
                  setLevel6('');
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label $required>Category</Label>
              <CategorySelect
                showSearch
                value={category || undefined}
                placeholder={
                  industry ? 'Search or select a category...' : 'Select industry first'
                }
                style={{ width: '100%' }}
                onChange={(value) => {
                  setCategory(value);
                  setLevel4('');
                  setLevel5('');
                  setLevel6('');
                }}
                optionFilterProp="children"
                disabled={!industry}
                filterOption={(input, option) =>
                  (option?.children ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {categoryOptions.map((opt) => (
                  <AntSelect.Option key={opt} value={opt}>
                    {opt}
                  </AntSelect.Option>
                ))}
              </CategorySelect>
            </FormGroup>

            {level4Options.length > 0 && (
              <FormGroup>
                <Label>Subcategory</Label>
                <StandardDropdown
                  value={level4}
                  placeholder="Select subcategory..."
                  options={level4Options.map((opt) => ({
                    value: opt,
                    label: opt,
                  }))}
                  onChange={(value) => {
                    setLevel4(value || '');
                    setLevel5('');
                    setLevel6('');
                  }}
                />
              </FormGroup>
            )}

            {level5Options.length > 0 && (
              <FormGroup>
                <Label>Detail</Label>
                <StandardDropdown
                  value={level5}
                  placeholder="Select detail..."
                  options={level5Options.map((opt) => ({
                    value: opt,
                    label: opt,
                  }))}
                  onChange={(value) => {
                    setLevel5(value || '');
                    setLevel6('');
                  }}
                />
              </FormGroup>
            )}

            {level6Options.length > 0 && (
              <FormGroup>
                <Label>Sub-detail</Label>
                <StandardDropdown
                  value={level6}
                  placeholder="Select sub-detail..."
                  options={level6Options.map((opt) => ({
                    value: opt,
                    label: opt,
                  }))}
                  onChange={(value) => setLevel6(value || '')}
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label>Folder URL (optional)</Label>
              <Input
                className="requirement-folder-url-input"
                value={folderUrl}
                onChange={(e) => setFolderUrl(e.target.value)}
                placeholder="https://drive.google.com/drive/folders/..."
              />
              <HelperText>
                Paste a shared Google Drive / SharePoint folder URL. Files will be
                uploaded for this requirement when you click Save.
              </HelperText>
            </FormGroup>

            {existingLinks.length > 0 && (
              <FormGroup>
                <Label>Linked Folders</Label>
                {existingLinks.map((link) => {
                  const key = link.id || link.url;
                  const isMarkedForDeletion = linksToDelete.includes(key);

                  return (
                    <div
                      key={key}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: 13,
                        marginBottom: 6,
                        gap: 8,
                      }}
                    >
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: isMarkedForDeletion ? '#9ca3af' : '#f97316',
                          textDecoration: isMarkedForDeletion
                            ? 'line-through'
                            : 'none',
                          flex: 1,
                          pointerEvents: isMarkedForDeletion ? 'none' : 'auto',
                        }}
                      >
                        📁 {link.name || link.url}
                      </a>
                      {link.type && (
                        <span
                          style={{
                            fontSize: 11,
                            color: '#6b7280',
                            marginRight: 4,
                          }}
                        >
                          {link.type}
                        </span>
                      )}

                      {!isMarkedForDeletion ? (
                        <RemoveLinkButton
                          type="button"
                          onClick={() => handleRemoveLink(key)}
                          title="Remove this link"
                        >
                          ×
                        </RemoveLinkButton>
                      ) : (
                        <UndoButton
                          type="button"
                          onClick={() => handleUndoRemoveLink(key)}
                          title="Undo removal"
                        >
                          Undo
                        </UndoButton>
                      )}
                    </div>
                  );
                })}
              </FormGroup>
            )}
          </DialogBody>

          <Footer>
            {requirement && (
              <DeleteButton
                type="button"
                onClick={handleDeleteRequirementClick}
                disabled={isSaving}
              >
                Delete
              </DeleteButton>
            )}

            <CancelButton type="button" onClick={onClose} disabled={isSaving}>
              Cancel
            </CancelButton>
            <SaveButton className="requirement-save-btn" type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </SaveButton>
          </Footer>
        </DialogContent>
      </DialogOverlay>

      <FloatingUploadModal
        visible={isUploadingFromDrive}
        message={`Uploading from ${uploadSource}...`}
      />
    </>
  );
};

export default RequirementDetailsDialog;
